import React, { useEffect, useState } from "react";
import "./styles/WebhooksPanel.css";
import { ManageApiService } from "../services/ManageApiService";

export interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  isActive: boolean;
  createdAt: string;
  // lastDeliveryAt?: string | null; // you can keep this in your model if you want, just unused
}

interface WebhooksPanelProps {
  showToast: (message: string) => void;
  onCopy: (value: string, label?: string) => Promise<void> | void;
}

const WebhooksPanel: React.FC<WebhooksPanelProps> = ({ showToast, onCopy }) => {
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);

  const [newWebhookName, setNewWebhookName] = useState("");
  const [newWebhookUrl, setNewWebhookUrl] = useState("");
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false);

  const [secretBanner, setSecretBanner] = useState<string | null>(null);
  const [secretBannerSource, setSecretBannerSource] = useState<
    "created" | "rotated" | null
  >(null);

  useEffect(() => {
    const fetchWebhooks = async () => {
      setLoading(true);
      const response = await ManageApiService.getWebhooks();
      if (!response.error) {
        setWebhooks(response.data?.webhooks ?? []);
      }
      setLoading(false);
    };

    fetchWebhooks();
  }, []);

  const handleCreateWebhook = async () => {
    if (!newWebhookName.trim()) {
      showToast("Please enter a name for the webhook.");
      return;
    }
    if (!newWebhookUrl.trim()) {
      showToast("Please enter a webhook URL.");
      return;
    }

    try {
      setIsCreatingWebhook(true);
      setSecretBanner(null);
      setSecretBannerSource(null);

      const response = await ManageApiService.createWebhook(
        newWebhookName.trim(),
        newWebhookUrl.trim()
      );

      if (response.error) {
        showToast("Failed to create webhook.");
        return;
      }

      const { secret } = response.data ?? {};
      if (!secret) {
        showToast("Webhook created, but secret was not returned.");
        return;
      }

    //   if (webhook) {
    //     setWebhooks((prev) => [webhook, ...prev]);
    //   }

      setNewWebhookName("");
      setNewWebhookUrl("");
      setSecretBanner(secret);
      setSecretBannerSource("created");
      showToast("Webhook created. Copy the secret and store it safely.");
    } finally {
      setIsCreatingWebhook(false);
    }
  };

  const handleRotateSecret = async (id: string) => {
    const webhook = webhooks.find((w) => w.id === id);
    const confirmed = window.confirm(
      `Rotate secret for webhook "${webhook?.name}"?\n\nExisting integrations will stop working until you update them with the new secret.`
    );
    if (!confirmed) return;

    try {
      setSecretBanner(null);
      setSecretBannerSource(null);

      const response = await ManageApiService.rotateWebhookSecret(id);
      
      if (response.error) {
        showToast("Failed to rotate webhook secret.");
        return;
      }

      const secret = response.data?.secret as string | undefined;
      if (!secret) {
        showToast("Secret rotated, but new secret was not returned.");
        return;
      }

      setSecretBanner(secret);
      setSecretBannerSource("rotated");
      showToast(
        "Webhook secret rotated. Update your receiver to use the new secret."
      );
    } catch {
      showToast("Failed to rotate webhook secret.");
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    const webhook = webhooks.find((w) => w.id === id);
    const confirmed = window.confirm(
      `Delete webhook "${webhook?.name}"? This will stop all deliveries to that endpoint.`
    );
    if (!confirmed) return;

    const response = await ManageApiService.deleteWebhook(id);
    if (response.error) {
      showToast("Failed to delete webhook.");
      return;
    }

    setWebhooks((prev) => prev.filter((w) => w.id !== id));
    showToast("Webhook deleted.");
  };

  const handleCopySecret = (secret: string) => {
    onCopy(secret, "Webhook secret");
  };

  return (
    <section className="api-card">
      <div className="api-card-header">
        <div>
          <h2 className="api-card-title">Webhooks</h2>
          <p className="api-card-subtitle">
            Configure webhook endpoints to receive events like{" "}
            <code>video.completed</code> and <code>video.failed</code>. Each
            webhook has its own signing secret, shown only once.
          </p>
        </div>
      </div>

      {/* Create webhook */}
      <div className="api-form-grid">
        <div className="api-form-field">
          <label className="api-label" htmlFor="webhookName">
            Webhook name
          </label>
          <input
            id="webhookName"
            className="api-input"
            placeholder="e.g. Main backend listener"
            value={newWebhookName}
            onChange={(e) => setNewWebhookName(e.target.value)}
          />
        </div>
        <div className="api-form-field">
          <label className="api-label" htmlFor="webhookUrl">
            Webhook endpoint URL
          </label>
          <input
            id="webhookUrl"
            className="api-input"
            placeholder="https://example.com/webhooks/handler"
            value={newWebhookUrl}
            onChange={(e) => setNewWebhookUrl(e.target.value)}
          />
        </div>
      </div>

      <p className="api-helper-text">
        Use HTTPS and keep this endpoint locked down on your side. The secret
        lets you verify that requests are really from this account.
      </p>

      <div className="api-actions-row api-webhook-actions-row">
        <button
          type="button"
          className="api-primary-button"
          disabled={isCreatingWebhook}
          onClick={handleCreateWebhook}
        >
          {isCreatingWebhook ? "Creating webhook..." : "Add webhook"}
        </button>
        <p className="api-warning-text">
          You’ll only see the webhook secret once after creation or rotation.
          Copy it immediately and store it somewhere safe.
        </p>
      </div>

      {secretBanner && (
        <div className="api-secret-banner">
          <div>
            <p className="api-secret-label">
              {secretBannerSource === "created"
                ? "New webhook signing secret (copy now)"
                : "Rotated webhook signing secret (copy now)"}
            </p>
            <code className="api-secret-value">{secretBanner}</code>
          </div>
          <button
            type="button"
            className="api-ghost-button"
            onClick={() => handleCopySecret(secretBanner)}
          >
            Copy
          </button>
        </div>
      )}

      {/* Existing webhooks list */}
      <div className="api-webhooks-list">
        <div className="api-webhooks-list-header">
          <h3 className="api-webhooks-list-title">Existing webhooks</h3>
          <p className="api-webhooks-list-subtitle">
            Rotate secrets when compromised and delete endpoints you no longer
            use.
          </p>
        </div>

        {loading ? (
          <p className="api-empty-state">Loading webhooks…</p>
        ) : webhooks.length === 0 ? (
          <p className="api-empty-state">
            You haven’t configured any webhooks yet.
          </p>
        ) : (
          <div
            className="api-webhooks-table"
            role="table"
            aria-label="Webhook endpoints"
          >
            <div className="api-webhooks-header-row" role="row">
              <div className="api-webhooks-header-cell" role="columnheader">
                Name
              </div>
              <div className="api-webhooks-header-cell" role="columnheader">
                URL
              </div>
              <div className="api-webhooks-header-cell" role="columnheader">
                Created
              </div>
              <div className="api-webhooks-header-cell" role="columnheader">
                Status
              </div>
              <div className="api-webhooks-header-cell" role="columnheader">
                Actions
              </div>
            </div>

            {webhooks.map((wh) => (
              <div className="api-webhooks-row" role="row" key={wh.id}>
                <div className="api-webhooks-cell" role="cell">
                  <span className="api-webhook-name">{wh.name}</span>
                </div>
                <div className="api-webhooks-cell" role="cell">
                  <span className="api-webhook-url" title={wh.url}>
                    {wh.url}
                  </span>
                </div>
                <div className="api-webhooks-cell" role="cell">
                  {wh.createdAt}
                </div>
                <div className="api-webhooks-cell" role="cell">
                  <span
                    className={`api-status-pill api-status-pill--${
                      wh.isActive ? "active" : "revoked"
                    }`}
                  >
                    {wh.isActive ? "Active" : "Disabled"}
                  </span>
                </div>
                <div
                  className="api-webhooks-cell api-webhooks-actions"
                  role="cell"
                >
                  <button
                    type="button"
                    className="api-ghost-button api-keys-action-button"
                    onClick={() => handleRotateSecret(wh.id)}
                  >
                    Rotate secret
                  </button>
                  <button
                    type="button"
                    className="api-danger-button api-keys-action-button"
                    onClick={() => handleDeleteWebhook(wh.id)}
                  >
                    Delete webhook
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default WebhooksPanel;
